/**
 * PuzzleRoyaleTournamentDetail
 *
 * Player-facing detail / pre-race lobby for ONE Puzzle Royale tournament,
 * keyed on the durable numeric `id`. This is the P5 "arena-style" landing
 * that discovery rows (and, in P8, the deep link) open. It is a PARALLEL
 * surface to the chess-tournament detail — it never touches the chess
 * tournament route / id space (skill.md pitfall 34).
 *
 * State-aware body (mirrors arena's register→lobby→play→results arc):
 *   scheduled → countdown + rules + roster + Register / Withdraw (D7/D8).
 *               Registration closes at start; late-comers spectate (D8).
 *   live      → "Join the race" (routes to FunChallengesLobby?join=<code>,
 *               which seeds the lobby and the global listener navigates into
 *               PuzzleRoyaleBattle). Unregistered / already-racing users can
 *               still open it to spectate.
 *   cancelled → terminal "called off" notice (+ reason if the socket carried
 *               one via funChallengesTournamentStore).
 *   completed → hand off to the P7 results view (rendered by the caller); here
 *               we just show a "view results" affordance.
 *
 * Reads: useRoyaleTournamentDetail (REST, polled while scheduled/live so the
 * roster count + a scheduled→live flip surface without a manual refresh).
 * Writes: register / withdraw via the SOCKET (useRoyaleSocket), same as the
 * host lifecycle actions. The live socket notice (fun_tournament_state) is
 * layered on top of the REST read so a scheduled→live flip is instant.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useNavigation} from '@react-navigation/native';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import CCLoader from '@/Atoms/CCLoader';
import {FontSizes, vs} from '@/Utils/Dimensions';
import ChessboardWithArrow from '@/Components/Chessboard';
import useChessboardColorTheme from '@/Hooks/ChessboardColorTheme/useChessboardColorTheme';

import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useRoyaleSocket from '@/Hooks/FunChallengesSocket/useRoyaleSocket';
import useFunChallengesTournamentStore from '@/Store/funChallengesTournamentStore';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
import {
  useRoyaleTournamentDetail,
  type RoyaleTournamentDetail,
  type RoyaleTournamentRosterEntry,
} from '@/Hooks/FunChallengesApi/useRoyaleTournaments';
import PuzzleRoyaleTournamentResults from './PuzzleRoyaleTournamentResults';
import {TOURNAMENT_START_LEAD_MS} from '../royaleConstants';
import {serverNowMs} from '../util/serverClock';
import PuzzleRoyaleBattle from '../PuzzleRoyaleBattle';
import useHydrateRoyaleFromResults from '../useHydrateRoyaleFromResults';
// The ad-hoc Fun Challenges lobby's roster — reused verbatim here so the
// registration waiting room looks identical to the lobby waiting room.
import RoyaleLobbyRoster from './RoyaleLobbyRoster';
// The SHARED page skeleton PuzzleRoyaleBattle renders through — guarantees this
// lobby is pixel-identical to the live one (same columns, gaps, board size).
import RoyaleLobbyShell, {useRoyaleBoardWidth} from './RoyaleLobbyShell';
import {
  buildTournamentLink,
  copyToClipboard,
  tournamentDisplayName,
} from '@/Screens/FunChallenges/util/tournamentLink';

const TEAL = '#3DAB9E';
const TEAL_DARK = '#1F6C63';
const TEAL_TINT_SOFT = 'rgba(61, 171, 158, 0.06)';
const BROWN = '#4D3F37';
const TEXT_DARK = '#262322';
const BORDER = 'rgba(38, 35, 34, 0.1)';
const RED = '#EB5757';
// Page background — matches RoyaleLobbyShell's BG_PAGE so the shell-hosting
// root and the shell itself are the same colour.
const BG_PAGE = '#FAF6EB';
// Arena-parity neutrals (same values the arena/leaderboard cards use, kept as
// royale-local constants so this screen doesn't depend on that theme module).
const LINE = '#F0E9DA'; // hairline card border
const MUTED = '#9A9184'; // uppercase section labels / secondary text
const STARTING_FEN =
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

type SchedTab = 'race' | 'players' | 'spectators' | 'details';

interface Props {
  tournamentId: number | string;
  /** Rendered when state === 'completed' — the P7 results view. */
  renderResults?: (detail: RoyaleTournamentDetail) => React.ReactNode;
}

function useCountdown(targetIso: string | null): string | null {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!targetIso) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  // `targetIso` is the SERVER's scheduled_at, so measure against the server's
  // clock (util/serverClock.ts). On a device running 5 min fast the raw-clock
  // version showed "Starting…" for a tournament still 4 minutes away.
  const ms = Date.parse(targetIso) - serverNowMs();
  if (isNaN(ms)) return null;
  if (ms <= 0) return 'Starting…';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function StatePill({state}: {state: string}) {
  const map: Record<string, {bg: string; label: string}> = {
    scheduled: {bg: TEAL, label: 'SCHEDULED'},
    live: {bg: '#E17846', label: 'LIVE'},
    completed: {bg: BROWN, label: 'COMPLETED'},
    cancelled: {bg: RED, label: 'CANCELLED'},
  };
  const s = map[state] || {bg: BROWN, label: state.toUpperCase()};
  return (
    <View style={[styles.statePill, {backgroundColor: s.bg}]}>
      <CCText fontSize={FontSizes[11]} color={CCColors.White} fontWeight="bold">
        {s.label}
      </CCText>
    </View>
  );
}

/** One label/value line in the Details tab's tournament-info block. */
function DetailRow({label, value}: {label: string; value: string}) {
  return (
    <View style={styles.detailRow}>
      <CCText style={styles.detailLabel}>{label}</CCText>
      {/* numberOfLines + flexShrink so a long name/host truncates instead of
          pushing the value off the right edge of the rail. */}
      <CCText style={styles.detailValue} numberOfLines={1}>
        {value}
      </CCText>
    </View>
  );
}

function InfoChip({icon, text}: {icon: string; text: string}) {
  return (
    <View style={styles.infoChip}>
      <MaterialCommunityIcons name={icon} size={14} color={TEAL_DARK} />
      <CCText fontSize={FontSizes[13]} color={TEAL_DARK}>
        {text}
      </CCText>
    </View>
  );
}

/** Settings chip under the lobby title — mirrors PuzzleRoyaleBattle's WaitChip
 *  (icon + label pill) so the scheduled card matches the live waiting room. */
function WaitChip({
  icon,
  text,
  capitalize,
}: {
  icon: string;
  text: string;
  capitalize?: boolean;
}) {
  return (
    <View style={styles.waitChip}>
      <MaterialCommunityIcons name={icon} size={13} color="#2E8B7F" />
      <CCText style={[styles.waitChipText, capitalize && styles.waitChipCap]}>
        {text}
      </CCText>
    </View>
  );
}

/** Register / Registered→Withdraw button, shared by the Race + Details tabs. */
function RegisterAction({
  iAmRegistered,
  busy,
  full,
  onRegister,
  onWithdraw,
}: {
  iAmRegistered: boolean;
  busy: boolean;
  full: boolean;
  onRegister: () => void;
  onWithdraw: () => void;
}) {
  if (iAmRegistered) {
    return (
      <View style={{gap: vs(6)}}>
        <View style={styles.registeredNote}>
          <MaterialCommunityIcons name="check-circle" size={18} color={TEAL} />
          <CCText fontSize={FontSizes[13]} color={TEAL_DARK} fontWeight="bold">
            You've joined
          </CCText>
        </View>
        <Pressable
          disabled={busy}
          onPress={onWithdraw}
          style={({pressed}) => [
            styles.actionBtn,
            styles.withdrawBtn,
            busy && styles.btnDisabled,
            pressed && !busy && {opacity: 0.9},
          ]}>
          <CCText fontSize={FontSizes[14]} color={CCColors.White} fontWeight="bold">
            {busy ? 'Working…' : 'Withdraw'}
          </CCText>
        </Pressable>
      </View>
    );
  }
  return (
    <Pressable
      disabled={busy || full}
      onPress={onRegister}
      style={({pressed}) => [
        styles.actionBtn,
        styles.registerBtn,
        (busy || full) && styles.btnDisabled,
        pressed && !busy && {opacity: 0.9},
      ]}>
      <CCText fontSize={FontSizes[16]} color={BROWN} fontWeight="bold">
        {full ? 'Tournament full' : busy ? 'Working…' : 'Join'}
      </CCText>
    </Pressable>
  );
}

export default function PuzzleRoyaleTournamentDetail({
  tournamentId,
  renderResults,
}: Props) {
  const navigation = useNavigation<any>();
  const {width} = useWindowDimensions();
  const isWide = width >= 900;
  const chessboardColorTheme = useChessboardColorTheme();
  // Board size from the SHARED royale sizing hook — identical to the live battle
  // lobby for the same viewport (flanked=false: board + rail, no grid column).
  const {boardWidth: shellBoardWidth} = useRoyaleBoardWidth(false);
  const [schedTab, setSchedTab] = useState<SchedTab>('details');
  // Accordion (#2): exactly ONE of the two explainer sections is open at a time.
  // "How it works" is expanded by default; opening "Scoring & streaks" collapses
  // it, and vice-versa — so the rail never becomes a long double-scroll.
  // Details accordion. Starts CLOSED (null) so the tab opens compact and the
  // tournament-info block is what you see first; opening one section closes the
  // other, and tapping an open header collapses it.
  const [openSection, setOpenSection] = useState<'how' | 'scoring' | null>(null);
  const howOpen = openSection === 'how';
  const scoringOpen = openSection === 'scoring';
  const toggleSection = (s: 'how' | 'scoring') =>
    setOpenSection(prev => (prev === s ? null : s));
  const {
    registerTournament,
    withdrawTournament,
    spectate,
    probeSession,
    authed,
  } = useRoyaleSocket();

  const myUserId = useLoggedInUserStore(
    s => (s.userData && (s.userData.user_id ?? s.userData.pk)) || s.userId || '',
  );
  const myName = useLoggedInUserStore(
    s => s.userData?.username || s.userData?.name || s.userData?.first_name || '',
  );
  const myIcon = useLoggedInUserStore(s => s.icon);

  // Register/withdraw errors (e.g. time_overlap) surface here as a banner.
  const errorMessage = useFunChallengesRoyaleStore(s => s.errorMessage);
  const clearError = useFunChallengesRoyaleStore(s => s.clearError);

  // Live socket notice for THIS tournament (scheduled→live flip carries the
  // join code + roomId; a cancel carries the reason). Layered on the REST read.
  const notice = useFunChallengesTournamentStore(
    s => s.stateById[String(tournamentId)],
  );

  // Poll while the tournament is pre-race so the roster count + a
  // scheduled→live flip surface without a manual refresh. Stop polling once
  // terminal (completed/cancelled) — nothing changes after that.
  // `isFetching` (not just `isLoading`) so the auto-route below can wait out a
  // background refetch of a stale cached entry — see the comment there.
  const {data: detail, refetch, isLoading, isFetching} =
    useRoyaleTournamentDetail(tournamentId, {refetchIntervalMs: 8_000});

  // True once the detail query has completed at least one fetch in THIS mount.
  // Until then a cached `detail.state` is not trustworthy (see effectiveState).
  // A ref, not state: it must not itself trigger a render, and the 8s poll would
  // otherwise flip it every tick.
  const hasSettledOnceRef = useRef(false);
  // Reset when the tournament CHANGES. `tournamentId` can change without a
  // remount (the stack navigator reuses this screen instance), so a ref that only
  // ever goes true made the NEXT tournament trust its stale cached state on the
  // first render — re-introducing the very registration-lobby flash this ref
  // exists to prevent (view A → back → open a completed B you'd previously seen
  // as `scheduled` → full Join lobby for a finished tournament, then results).
  useEffect(() => {
    hasSettledOnceRef.current = false;
  }, [tournamentId]);
  useEffect(() => {
    if (!isFetching && detail) hasSettledOnceRef.current = true;
  }, [isFetching, detail]);

  // Effective state: prefer a fresher live socket notice over the REST read
  // (the REST poll can lag the scheduled→live flip by up to the poll interval).
  const effectiveState = useMemo(() => {
    const rest = detail?.state;
    const live = notice?.state;
    // REST wins once it reports a TERMINAL state. The socket notice is cached in
    // a per-tournament map for the whole session and there is NO 'completed'
    // broadcast (mark_ended doesn't send one — only live/cancelled are
    // broadcast), so a 'live' notice from the start of the race outlives the
    // race itself. Letting it outrank REST kept the screen in live mode after
    // the tournament finished, so the auto-route below fired goPlayLive() on a
    // dead room and the server answered ROOM_NOT_WAITING → "This race has
    // already started — ask the host for a spectate link". A finished
    // tournament must render its results, not try to join.
    if (rest === 'completed' || rest === 'cancelled') return rest;
    // A cached notice must NOT outrank REST before REST has answered. Opening a
    // FINISHED tournament from the Joined list is a cold mount: `rest` is still
    // undefined for the first fetch, while `notice.state` is the 'live' from when
    // the race began (per-session map, and mark_ended broadcasts no 'completed',
    // so it never gets cleared). That made effectiveState 'live' for the first
    // render, which fired the auto-route below and navigated the user away —
    // then REST arrived saying 'completed' and the screen corrected itself. That
    // is the split-second wrong-screen hop. With no REST state yet we simply
    // don't know, so render nothing decisive and wait one fetch.
    // Same reasoning for a STALE CACHED read. react-query serves the previous
    // entry synchronously (staleTime 5s) while it refetches, and `isLoading` is
    // false in that case. So re-opening a tournament you had viewed earlier while
    // it was 'scheduled' replayed 'scheduled' for one render — which rendered the
    // REGISTRATION page — before the refetch corrected it to 'completed'. That is
    // the "goes to registration page then the correct screen" flash. Treat a
    // first, not-yet-revalidated read as unknown.
    if (!rest || (isFetching && !hasSettledOnceRef.current)) return undefined;
    // Only the live/cancelled transitions from the socket outrank a
    // non-terminal REST read; a 'registered'/'withdrawn' roster notice is not a
    // tournament state.
    if (live === 'live' || live === 'cancelled') return live;
    return rest;
    // `isFetching` is a dep so this recomputes when the first fetch settles —
    // the ref alone cannot trigger a render.
  }, [detail?.state, notice?.state, isFetching]);

  // Refetch when the socket says the state changed (keeps roster/results fresh).
  useEffect(() => {
    if (
      notice?.state === 'live' ||
      notice?.state === 'completed' ||
      notice?.state === 'cancelled'
    ) {
      refetch();
    }
  }, [notice?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const countdown = useCountdown(
    effectiveState === 'scheduled' ? detail?.scheduled_at ?? null : null,
  );

  // COMPLETED: seed the royale store from the REST results so the ORDINARY
  // PuzzleRoyaleBattle ended screen can render this tournament — instead of a
  // second, hand-maintained review layout. Unconditional (Rules of Hooks); the
  // hook itself no-ops unless there are results AND no live race is in flight,
  // and returns whether the store is ready.
  const hydratedFromResults = useHydrateRoyaleFromResults(
    effectiveState === 'completed' ? detail : null,
    String(myUserId),
  );

  const roster = detail?.roster || [];
  const registered = roster.filter(r => r.status === 'registered');
  const myEntry = registered.find(r => String(r.user_id) === String(myUserId));
  const iAmRegistered = !!myEntry;

  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Ticks the shared lobby roster's countdown pill (it renders "Starts in m:ss"
  // from scheduledStartMs). Only runs while a scheduled start is pending.
  //
  // Count down to the moment the race ACTUALLY begins, which is
  // `scheduled_at - TOURNAMENT_START_LEAD_MS`, not `scheduled_at` itself. The
  // server arms the start timer 10s early (arena parity) to cover room create,
  // seating, the mark_started CAS and the ~2.8s puzzle-sequence fetch. Counting
  // to the raw scheduled_at made the pill get cut off around 0:10 as the screen
  // swapped to the 3-2-1 overlay — it read as "starts in 12..11..10 then
  // immediately starts". Subtracting the lead makes the pill hand off to the
  // 3-2-1 at zero instead of at ten.
  const rawScheduledMs = detail?.scheduled_at
    ? Date.parse(detail.scheduled_at)
    : 0;
  const scheduledStartMs = rawScheduledMs
    ? rawScheduledMs - TOURNAMENT_START_LEAD_MS
    : 0;
  // Server-clock "now": this feeds RoyaleLobbyRoster's countdown pill, which
  // computes `expiresAt - nowMs` against the server-derived scheduled start.
  // Using the raw device clock here made a skewed machine show a wrong (or
  // already-expired) "Starts in m:ss".
  const [nowMs, setNowMs] = useState(() => serverNowMs());
  useEffect(() => {
    if (effectiveState !== 'scheduled' || !scheduledStartMs) return;
    const id = setInterval(() => setNowMs(serverNowMs()), 1000);
    return () => clearInterval(id);
  }, [effectiveState, scheduledStartMs]);

  /**
   * The Django roster mapped onto the ad-hoc lobby's player shape, so the SAME
   * `RoyaleLobbyRoster` component renders both waiting rooms (avatar colour by
   * join order, "You" highlight, HOST badge). Registration order is join order.
   */
  const rosterAsLobbyPlayers = useMemo(
    () =>
      registered.map((r: RoyaleTournamentRosterEntry) => ({
        userId: String(r.user_id),
        name: r.user_name || `Player ${r.user_id}`,
        isHost: !!detail && String(r.user_id) === String(detail.created_by),
      })),
    [registered, detail?.created_by],
  );

  const onCopyLink = () => {
    if (copyToClipboard(buildTournamentLink(tournamentId))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const onRegister = () => {
    setBusy(true);
    registerTournament(String(tournamentId), myName || undefined);
    setTimeout(() => {
      setBusy(false);
      refetch();
    }, 900);
  };

  const onWithdraw = () => {
    setBusy(true);
    withdrawTournament(String(tournamentId));
    setTimeout(() => {
      setBusy(false);
      refetch();
    }, 900);
  };

  // Live entry code: prefer the fresh socket notice, else the REST share_code.
  const joinCode = notice?.shareCode || detail?.share_code || '';

  // Registered players PLAY; everyone else WATCHES (D8: registration closes at
  // start; late-comers spectate). Spectate sends fun_room_spectate directly —
  // the server replies fun_spectate_start, the store flips, and the global
  // listener navigates to PuzzleRoyaleBattle in read-only mode.
  //
  // A REGISTERED player does NOTHING here — they are already seated server-side.
  //
  // History: this first routed to `FunChallengesLobby?autoJoin=1`, then (Round-70)
  // called `joinLobby()` + navigated directly. Both were wrong for a seated
  // player. The server broadcasts `live` BEFORE `beginSession` runs, so this
  // fires ~2.8s (the generateSequence fetch) before the session exists:
  //   - `joinLobby()` seeds an OPTIMISTIC lobby (`hostId:''`, `settings:{}`) —
  //     that empty-looking screen was the "odd flash", and it also fires a
  //     redundant `fun_room_join` for someone already in the room.
  //   - navigating early just moved the empty wait to the battle screen.
  // So: stay put. `fun_session_start` flips the store to 'in_battle' and the
  // global listener navigates into a FULLY HYDRATED battle screen. The probe
  // effect below is the safety net if that envelope is ever missed.
  //
  // NOTE this is registered-players-only. A late-comer is NOT seated, so nothing
  // would ever push them anywhere — `goSpectateLive` still joins + navigates.
  const goPlayLive = useCallback(() => {
    if (!joinCode || !authed) return;
    probeSession();
  }, [joinCode, authed, probeSession]);

  const goSpectateLive = useCallback(() => {
    if (!joinCode || !authed) return;
    spectate(joinCode, {username: myName || undefined, avatarUrl: myIcon});
    // The global listener navigates on the spectate-session store flip.
  }, [joinCode, authed, spectate, myName, myIcon]);

  // AUTO-ROUTE on live: the moment we know the tournament is live + have the
  // code, take the user straight in — registered → play, else → spectate. No
  // intermediate "Join" click (the detail card is just a brief pass-through for
  // a live tournament). One-shot guard so a re-render / poll doesn't re-fire.
  const autoRoutedRef = useRef(false);
  useEffect(() => {
    if (effectiveState !== 'live' || !joinCode || autoRoutedRef.current) return;
    // Wait until the roster read resolved so iAmRegistered is trustworthy.
    if (isLoading) return;
    // Never auto-route off a STALE cached read. `isLoading` is false when
    // react-query serves a cached entry, so re-opening a tournament you watched
    // while it was live replays `state: 'live'` for one render (staleTime 5s) and
    // would navigate you into a dead room before the refetch corrects it to
    // 'completed'. Requiring a settled, non-fetching read costs one tick and
    // removes the wrong-screen hop entirely.
    if (isFetching) return;
    // BOTH paths now need a live socket (play joins the room directly rather
    // than delegating to the lobby screen), so gate on `authed` BEFORE setting
    // the one-shot flag — otherwise an unauthed first tick would burn the flag
    // and we'd never route at all.
    if (!authed) return;
    autoRoutedRef.current = true;
    if (iAmRegistered) goPlayLive();
    else goSpectateLive();
  }, [
    effectiveState,
    joinCode,
    isLoading,
    // MUST be a dep: the effect bails while a refetch is in flight, so it has to
    // re-run when that settles or a genuinely live tournament would never route.
    isFetching,
    iAmRegistered,
    authed,
    goPlayLive,
    goSpectateLive,
  ]);

  // SAFETY NET for the registered-player path. Since we no longer navigate
  // eagerly, a MISSED `fun_session_start` (dropped envelope, socket reconnect
  // mid-start) would otherwise leave a seated player parked on this page for the
  // whole race. Re-probe on a short cadence while the tournament reads `live`
  // and the store still has no session; the probe resolves their active session
  // and the global listener then navigates them in. Stops as soon as the store
  // leaves 'idle', and is bounded so a genuinely dead room can't spin forever.
  // (`NO_ACTIVE_SESSION` is swallowed for probe requests, so this is silent.)
  //
  // ⚠️ `status !== 'idle'` is NOT sufficient on its own. A REVIEW SNAPSHOT sits
  // in `status: 'ended'` (Round-69: `useHydrateRoyaleFromResults` seeds the ONE
  // global royale store to render a completed tournament). So a registered
  // player who opened an old tournament while waiting for theirs to start had
  // this net cancel itself on its FIRST tick — the exact "missed
  // fun_session_start" case it exists for, silently disabled. A review snapshot
  // holds no live session, so treat it as idle-equivalent and keep probing.
  useEffect(() => {
    if (effectiveState !== 'live' || !iAmRegistered || !authed) return;
    let probes = 0;
    const id = setInterval(() => {
      probes += 1;
      const st = useFunChallengesRoyaleStore.getState();
      const holdsNoLiveSession = st.status === 'idle' || st.isReviewSnapshot;
      if (!holdsNoLiveSession || probes > 8) {
        clearInterval(id);
        return;
      }
      probeSession();
    }, 1200);
    return () => clearInterval(id);
  }, [effectiveState, iAmRegistered, authed, probeSession]);

  // "STARTING" — a registered player whose tournament just flipped to `live`
  // but whose session hasn't arrived yet (the server broadcasts `live` ~2.8s
  // before `fun_session_start`; see pitfall 107).
  //
  // Previously `live` swapped the whole lobby out for a bare "Join the race"
  // card, so at T-0 the user watched the rich lobby vanish, a stub card appear,
  // and then got navigated again — the jarring double-transition. Now we KEEP
  // the lobby exactly as it is and just relabel the countdown pill, so the only
  // visible change is "Starting…" until the battle screen takes over.
  const isStartingForMe =
    effectiveState === 'live' && iAmRegistered && !hydratedFromResults;

  // Hold the loading state until the tournament's state is actually KNOWN.
  //
  // `effectiveState` is deliberately `undefined` until the first REST read
  // settles (a cached 'live'/'scheduled' notice must not outrank an unfetched
  // REST state). But the branches below are inline ternaries inside ONE tree,
  // not early returns — so with `effectiveState` undefined, neither the
  // `scheduled` nor the `completed` branch matched while the page still rendered
  // its header/tabs/registration-shaped chrome. Opening a COMPLETED tournament
  // therefore flashed the registration screen for a frame before the results
  // appeared. Gate on the resolved state, not just on `detail` existing.
  // ORDER MATTERS: a FAILED fetch leaves `detail` AND `effectiveState` both
  // undefined with `isLoading` false. "Tournament not found" has to win that
  // case, otherwise the loading guard below would spin forever.
  if (!isLoading && !detail) {
    return (
      <View style={styles.centered}>
        <CCText color={CCColors.TextColor.GreyText}>
          Tournament not found.
        </CCText>
      </View>
    );
  }
  if (isLoading || !effectiveState) {
    return (
      <View style={styles.centered}>
        {/* Same in-page spinner the battle screen and BattleScreen use, so the
            royale surfaces share one loading treatment. */}
        <CCLoader loading isComponentLoader />
      </View>
    );
  }
  if (!detail) {
    return (
      <View style={styles.centered}>
        <CCText color={CCColors.TextColor.GreyText}>
          Tournament not found.
        </CCText>
      </View>
    );
  }

  const topics = detail.topics && detail.topics.length ? detail.topics : null;

  // SCHEDULED and COMPLETED render the full-height royale shell, which needs a
  // bounded parent height (its rail owns an internal ScrollView and its layout
  // is flex:1). Wrapping those in the page ScrollView collapsed the shell to
  // content height — the board grew past the viewport and got clipped, and the
  // rail shrank to a thin strip instead of filling the column. Mirrors
  // PuzzleRoyaleBattle, whose root is a plain View for exactly this reason.
  // `completed` only takes the full-height root once the store is hydrated —
  // that's when PuzzleRoyaleBattle renders (it needs a bounded parent height,
  // same as the shell). Without results we fall back to the scrolling summary.
  const usesShell =
    effectiveState === 'scheduled' ||
    isStartingForMe ||
    (effectiveState === 'completed' && hydratedFromResults);
  const Root: any = usesShell ? View : ScrollView;
  const rootProps = usesShell
    ? {style: styles.rootFlex}
    : {style: styles.root, contentContainerStyle: styles.content};

  return (
    <Root {...rootProps}>
      {errorMessage ? (
        <Pressable onPress={clearError} style={styles.errorBanner}>
          <MaterialCommunityIcons name="alert-circle" size={16} color={RED} />
          <CCText fontSize={FontSizes[12]} color={RED} style={{flex: 1}}>
            {errorMessage}
          </CCText>
          <CCText fontSize={FontSizes[11]} color={RED}>
            Dismiss
          </CCText>
        </Pressable>
      ) : null}
      {/* Back + state pill row. SCHEDULED puts title/host into the left lobby
          card, and COMPLETED renders the full-height review shell (which owns
          its own header + floating Back) — stacking this boxed card above
          either one is what made the ended screen look broken: the card ate the
          top of the viewport and squashed the shell below it. */}
      {!usesShell ? (
        <Box style={styles.card}>
          <View style={styles.headerRow}>
            <View style={{flex: 1, gap: 4}}>
              <CCText fontSize={FontSizes[18]} fontWeight="bold" color={TEXT_DARK}>
                {tournamentDisplayName(detail)}
                {detail.is_private ? ' 🔒' : ''}
              </CCText>
              <CCText
                fontSize={FontSizes[12]}
                color={CCColors.TextColor.GreyText}>
                {`Hosted by ${detail.created_by_name || detail.created_by}`}
              </CCText>
            </View>
            <View style={styles.headerRight}>
              <StatePill state={effectiveState || 'scheduled'} />
              {effectiveState !== 'cancelled' ? (
                <Pressable onPress={onCopyLink} style={styles.copyLinkBtn}>
                  <MaterialCommunityIcons
                    name={copied ? 'check' : 'link-variant'}
                    size={14}
                    color={TEAL_DARK}
                  />
                  <CCText fontSize={FontSizes[11]} color={TEAL_DARK}>
                    {copied ? 'Copied' : 'Copy link'}
                  </CCText>
                </Pressable>
              ) : null}
            </View>
          </View>
          <View style={styles.chipRow}>
            <InfoChip icon="speedometer" text={detail.difficulty} />
            <InfoChip icon="timer-outline" text={`${detail.timer_sec}s`} />
            <InfoChip
              icon="account-group"
              text={`${registered.length}/${detail.max_players}`}
            />
            {topics ? (
              <InfoChip icon="tag-multiple" text={`${topics.length} topics`} />
            ) : (
              <InfoChip icon="tag-multiple" text="Any topic" />
            )}
          </View>
        </Box>
      ) : null}

      {/* ── SCHEDULED: EXACT PuzzleRoyaleBattle lobby look ──────────────────
          Left = title card (name + settings chips, waitCard style) above the
          static starting-position board. Right = the same railTabs bar as the
          battle lobby, with 4 tabs Race / Players / Spectators / Details.
          Lands on DETAILS. Register / Withdraw is PERSISTENT below the tab body
          (never scrolls out of the tab). */}
      {effectiveState === 'scheduled' || isStartingForMe ? (
        <>
          {/* No Back link. The left nav is always present, so this screen never
              needed its own — and every attempt to route it landed somewhere
              wrong (it kept opening the CHESS tournament of the same id via
              react-navigation's param merge). Removed at the user's request. */}
          {/* Rendered through the SHARED RoyaleLobbyShell — the same skeleton
              PuzzleRoyaleBattle uses — so this lobby is pixel-identical to the
              live one (same paddings, gaps, column flex ratios, board size and
              rail width) instead of relying on a parallel set of styles. */}
          <RoyaleLobbyShell
            aboveBoard={
              <View style={[styles.waitCard, {width: shellBoardWidth}]}>
                <View style={styles.waitTitleRow}>
                  <CCText style={styles.waitCardTitle} numberOfLines={1}>
                    {tournamentDisplayName(detail)}
                    {detail.is_private ? ' 🔒' : ''}
                  </CCText>
                  <StatePill state="scheduled" />
                </View>
                <View style={styles.waitChips}>
                  <WaitChip icon="timer-outline" text={`${detail.timer_sec}s`} />
                  <WaitChip icon="speedometer" text={detail.difficulty} capitalize />
                  <WaitChip
                    icon="tag-multiple"
                    text={topics ? `${topics.length} topics` : 'All topics'}
                  />
                  <WaitChip
                    icon="account-group"
                    text={`${registered.length} player${
                      registered.length === 1 ? '' : 's'
                    }`}
                  />
                </View>
              </View>
            }
            board={
              /* Sized EXACTLY as the battle lobby does it: a wrapper View with
                 an explicit width (the board fills its parent), not a prop. */
              <View
                style={{width: shellBoardWidth, alignSelf: 'center'}}
                pointerEvents="none">
                <ChessboardWithArrow
                  position={STARTING_FEN}
                  boardOrientation="white"
                  colorTheme={chessboardColorTheme}
                  disabled
                  animationDuration={0}
                />
              </View>
            }
            rail={
              <View style={styles.rail}>
                <View style={styles.railTabs}>
                {(
                  [
                    {key: 'race', label: 'Race'},
                    {key: 'players', label: `Players · ${registered.length}`},
                    {key: 'spectators', label: 'Spectators'},
                    {key: 'details', label: 'Details'},
                  ] as Array<{key: SchedTab; label: string}>
                ).map(t => {
                  const active = schedTab === t.key;
                  return (
                    <Pressable
                      key={t.key}
                      onPress={() => setSchedTab(t.key)}
                      style={[styles.railTab, active && styles.railTabActive]}>
                      <CCText
                        style={[
                          styles.railTabText,
                          active && styles.railTabTextActive,
                        ]}>
                        {t.label}
                      </CCText>
                    </Pressable>
                  );
                })}
              </View>

              <View style={styles.railBody}>
                {/* ── Race tab: pre-start placeholder ──
                    The countdown moved to Details (it's tournament INFO, not
                    race state); Race now mirrors the live screen's Race tab,
                    which is empty until there's an actual race to show. */}
                {schedTab === 'race' ? (
                  <View style={[styles.tabBody, styles.tabCard]}>
                    <View style={styles.emptyState}>
                      <MaterialCommunityIcons
                        name="flag-checkered"
                        size={28}
                        color="#C9BE9E"
                      />
                      <CCText
                        fontSize={FontSizes[13]}
                        color={CCColors.TextColor.GreyText}
                        style={{textAlign: 'center', lineHeight: 19}}>
                        The race track appears here once the game starts.
                      </CCText>
                    </View>
                  </View>
                ) : null}


                {/* ── Players tab: registered roster ──
                    Renders the EXACT SAME `RoyaleLobbyRoster` the ad-hoc Fun
                    Challenges lobby uses, so the two waiting rooms are visually
                    identical (same rows, avatar colours, HOST badge, countdown
                    pill). Differences are passed as props, not forked markup:
                    the pill counts down to the scheduled START rather than a
                    lobby expiry, and the footer is Register/Withdraw instead of
                    the host's Start button. */}
                {schedTab === 'players' ? (
                  <View style={[styles.tabBody, styles.tabCard]}>
                    {registered.length === 0 ? (
                      <View style={styles.emptyState}>
                        <MaterialCommunityIcons
                          name="account-group-outline"
                          size={28}
                          color="#C9BE9E"
                        />
                        <CCText
                          fontSize={FontSizes[13]}
                          color={CCColors.TextColor.GreyText}>
                          No one has joined yet — be the first!
                        </CCText>
                      </View>
                    ) : (
                      <RoyaleLobbyRoster
                        players={rosterAsLobbyPlayers}
                        myUserId={String(myUserId)}
                        isHost={false}
                        countdownMode="start"
                        expiresAt={scheduledStartMs}
                        nowMs={nowMs}
                        onStart={() => {}}
                        footer={null}
                      />
                    )}
                  </View>
                ) : null}

                {/* ── Spectators tab: none until live ── */}
                {schedTab === 'spectators' ? (
                  <View style={[styles.tabBody, styles.tabCard]}>
                    <View style={styles.emptyState}>
                      <MaterialCommunityIcons
                        name="eye-outline"
                        size={28}
                        color="#C9BE9E"
                      />
                      <CCText
                        fontSize={FontSizes[13]}
                        color={CCColors.TextColor.GreyText}
                        style={{textAlign: 'center', lineHeight: 19}}>
                        Spectators can watch once the race starts. Share the
                        invite link from Details so people can follow along live.
                      </CCText>
                    </View>
                  </View>
                ) : null}

                {/* ── Details tab (default landing): share + desc + rules +
                    collapsible scoring ── */}
                {schedTab === 'details' ? (
                  <View style={[styles.tabBody, styles.tabCard]}>
                    {/* Countdown lives HERE (moved off the Race tab): it's
                        tournament information, and Details is the tab this
                        screen lands on, so the most time-critical fact is
                        visible immediately instead of a tab away. */}
                    <View style={styles.countdownBlock}>
                      <CCText style={styles.tabSectionTitle}>
                        Game starts in
                      </CCText>
                      <CCText
                        fontSize={FontSizes[32]}
                        fontWeight="bold"
                        color={TEAL_DARK}>
                        {/* Once the clock reaches 0 the state flips scheduled →
                            live, so useCountdown is handed null and its own
                            'starting…' branch never renders. Fall back to
                            'Starting…' rather than a bare em-dash, which read as
                            "no information" at exactly the moment the race was
                            in fact beginning. */}
                        {countdown || 'Starting…'}
                      </CCText>
                      {detail.scheduled_at ? (
                        <CCText
                          fontSize={FontSizes[12]}
                          color={CCColors.TextColor.GreyText}>
                          {new Date(detail.scheduled_at).toLocaleString()}
                        </CCText>
                      ) : null}
                    </View>

                    {/* TOURNAMENT INFO — name, host, start time and settings as
                        label/value rows. Always visible (it's the identity of
                        the thing you're looking at); the two explainers below
                        are collapsed by default. */}
                    <View style={styles.infoBlock}>
                      <CCText style={styles.tabSectionTitle}>Tournament</CCText>
                      <DetailRow
                        label="Name"
                        value={tournamentDisplayName(detail)}
                      />
                      {/* "Created by" intentionally omitted — royale hosts are
                          staff, so surfacing an internal name/id to players adds
                          nothing. Kept out of the post-race Details tab too. */}
                      <DetailRow
                        label="Starts"
                        value={
                          detail.scheduled_at
                            ? new Date(detail.scheduled_at).toLocaleString()
                            : '—'
                        }
                      />
                      <DetailRow
                        label="Time control"
                        value={`${detail.timer_sec}s`}
                      />
                      <DetailRow label="Difficulty" value={detail.difficulty} />
                      <DetailRow
                        label="Players"
                        value={`${registered.length} of ${detail.max_players}`}
                      />
                      <DetailRow
                        label="Topics"
                        value={topics ? `${topics.length} selected` : 'Any topic'}
                      />
                      {/* Be-here-early notice. There is deliberately NO guard
                          stopping a registered player from starting another game
                          just before the race (see the overlap-guard DECISION in
                          the docs), so this sets the expectation instead: if you
                          are mid-game when the race begins you will miss the
                          start. Phrased as guidance, not a rule, because nothing
                          enforces it. */}
                      <View style={styles.beEarlyRow}>
                        <MaterialCommunityIcons
                          name="clock-alert-outline"
                          size={14}
                          color={TEAL_DARK}
                        />
                        <CCText style={styles.beEarlyText}>
                          Be on this page at least 2 minutes before the start
                          time. If you're in another game when the game begins,
                          you'll miss the puzzle royale.
                        </CCText>
                      </View>
                    </View>

                    <Pressable onPress={onCopyLink} style={styles.shareRow}>
                      <MaterialCommunityIcons
                        name={copied ? 'check' : 'link-variant'}
                        size={16}
                        color={TEAL_DARK}
                      />
                      <CCText
                        fontSize={FontSizes[13]}
                        color={TEAL_DARK}
                        fontWeight="bold">
                        {copied ? 'Link copied' : 'Copy invite link'}
                      </CCText>
                    </Pressable>

                    {detail.description && detail.description.trim() ? (
                      <View style={styles.descBlock}>
                        <CCText
                          fontSize={FontSizes[13]}
                          color={TEXT_DARK}
                          style={{lineHeight: 19}}>
                          {detail.description.trim()}
                        </CCText>
                      </View>
                    ) : null}

                    {/* How it works — COLLAPSIBLE, open by default (#2). */}
                    <Pressable
                      onPress={() => toggleSection('how')}
                      style={styles.scoringHeader}>
                      <CCText style={styles.cardTitle}>How it works</CCText>
                      <MaterialCommunityIcons
                        name={howOpen ? 'chevron-up' : 'chevron-down'}
                        size={18}
                        color={TEAL_DARK}
                      />
                    </Pressable>
                    {howOpen ? (
                    <View style={styles.ruleList}>
                      {[
                        {
                          icon: 'flag-checkered',
                          t: `Solve as many puzzles as you can in ${detail.timer_sec}s — whoever scores the most points wins.`,
                        },
                        {
                          icon: 'account-group',
                          t: `Up to ${detail.max_players} players — final rank is by total points (ties broken by who reached the score first).`,
                        },
                        {
                          icon: 'clock-start',
                          t: 'Auto-starts at the scheduled time (a host may start it early).',
                        },
                        {
                          icon: 'lock-outline',
                          t: 'While registered you can’t register for another Puzzle Royale tournament at the same time.',
                        },
                        {
                          icon: 'eye-outline',
                          t: 'Miss the start? You can still watch it live.',
                        },
                      ].map(r => (
                        <View key={r.t} style={styles.ruleRow}>
                          <MaterialCommunityIcons
                            name={r.icon}
                            size={16}
                            color={TEAL_DARK}
                          />
                          <CCText
                            fontSize={FontSizes[12]}
                            color={CCColors.TextColor.GreyText}
                            style={{flex: 1}}>
                            {r.t}
                          </CCText>
                        </View>
                      ))}
                    </View>
                    ) : null}

                    {/* Scoring & streaks — COLLAPSIBLE (#4). Mutually exclusive
                        with "How it works" above (#2). */}
                    <Pressable
                      onPress={() => toggleSection('scoring')}
                      style={styles.scoringHeader}>
                      <CCText style={styles.cardTitle}>Scoring &amp; streaks</CCText>
                      <MaterialCommunityIcons
                        name={scoringOpen ? 'chevron-up' : 'chevron-down'}
                        size={18}
                        color={TEAL_DARK}
                      />
                    </Pressable>
                    {scoringOpen ? (
                      <>
                        <CCText
                          fontSize={FontSizes[12]}
                          color={CCColors.TextColor.GreyText}
                          style={{lineHeight: 18}}>
                          Every correct solve raises your combo by 1 and scores
                          points. A wrong solve resets your combo to 0 (you keep
                          your points). The longer your streak, the more each
                          solve is worth:
                        </CCText>
                        <View style={styles.ladderTable}>
                          <View style={[styles.ladderRow, styles.ladderHead]}>
                            <CCText style={styles.ladderCellL}>
                              Combo streak
                            </CCText>
                            <CCText style={styles.ladderCellR}>
                              Points / solve
                            </CCText>
                          </View>
                          {[
                            {range: '1–2 in a row', pts: '1'},
                            {range: '3–4 in a row', pts: '2'},
                            {range: '5–9 in a row', pts: '3'},
                            {range: '10–14 in a row', pts: '4'},
                            {range: '15+ in a row', pts: '6'},
                          ].map(r => (
                            <View key={r.range} style={styles.ladderRow}>
                              <CCText style={styles.ladderCellL}>
                                {r.range}
                              </CCText>
                              <View style={styles.ladderCellRWrap}>
                                <CCText
                                  style={styles.ladderPts}>{`+${r.pts}`}</CCText>
                              </View>
                            </View>
                          ))}
                        </View>
                        <CCText
                          fontSize={FontSizes[11]}
                          color={CCColors.TextColor.GreyText}>
                          Example: solve 5 in a row → the 5th solve is worth 3
                          points. A 6-solve streak keeps scoring 3 each until you
                          reach 10.
                        </CCText>
                      </>
                    ) : null}
                  </View>
                ) : null}
              </View>

                {/* Persistent Register / Withdraw BELOW the tab body (#3). */}
                <View style={styles.railFooter}>
                  <RegisterAction
                    iAmRegistered={iAmRegistered}
                    busy={busy}
                    full={registered.length >= detail.max_players}
                    onRegister={onRegister}
                    onWithdraw={onWithdraw}
                  />
                </View>
              </View>
            }
          />
        </>
      ) : null}

      {/* ── LIVE: brief pass-through (auto-routes to play/spectate) ────────
          Skipped for a registered player mid-start — they keep the full lobby
          (isStartingForMe) instead of this stub card. Spectators and anyone who
          navigates back here still get it. */}
      {effectiveState === 'live' && !isStartingForMe ? (
        <Box style={styles.card}>
          <CCText style={styles.cardTitle}>The race is on</CCText>
          <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.GreyText}>
            {iAmRegistered
              ? 'Taking you into your race…'
              : 'Registration has closed — taking you in to watch live…'}
          </CCText>
          {/* Manual fallback in case the auto-route was blocked (e.g. socket
              still connecting) or the user navigated back here. */}
          <Pressable
            disabled={!joinCode}
            onPress={iAmRegistered ? goPlayLive : goSpectateLive}
            style={({pressed}) => [
              styles.actionBtn,
              styles.liveBtn,
              !joinCode && styles.btnDisabled,
              pressed && joinCode && {opacity: 0.9},
            ]}>
            <MaterialCommunityIcons
              name={iAmRegistered ? 'play' : 'eye-outline'}
              size={16}
              color={CCColors.White}
            />
            <CCText
              fontSize={FontSizes[15]}
              color={CCColors.White}
              fontWeight="bold">
              {iAmRegistered ? 'Join the race' : 'Watch live'}
            </CCText>
          </Pressable>
        </Box>
      ) : null}

      {effectiveState === 'cancelled' ? (
        <Box style={[styles.card, styles.cancelledCard]}>
          <MaterialCommunityIcons name="calendar-remove" size={22} color={RED} />
          <CCText fontSize={FontSizes[15]} fontWeight="bold" color={RED}>
            This tournament was called off
          </CCText>
          <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.GreyText}>
            {notice?.reason ||
              'The host cancelled it, or too few players registered to start.'}
          </CCText>
        </Box>
      ) : null}

      {effectiveState === 'completed' ? (
        renderResults ? (
          <>{renderResults(detail)}</>
        ) : hydratedFromResults ? (
          <>
            {/* No Back link here: the battle screen owns this viewport and has
                its own Exit control, and the floating overlay collided with the
                puzzle grid's top-left corner. */}
            {/* THE REAL ended screen. `useHydrateRoyaleFromResults` has seeded
                the royale store from `detail.results` and flipped status to
                'ended', so PuzzleRoyaleBattle renders its own post-game view —
                puzzle grid with green/red, reviewable board, final standings —
                with ZERO duplicated layout. Any future fix to the live ended
                screen lands here automatically. */}
            <PuzzleRoyaleBattle />
          </>
        ) : (
          /* No joined results (legacy/orphaned session) — the summary card is
             all we can show. `usesShell` is false in this case, so the root is
             already the scrolling page and the boxed header above still renders. */
          <PuzzleRoyaleTournamentResults detail={detail} />
        )
      ) : null}
    </Root>
  );
}

const styles = StyleSheet.create({
  root: {flex: 1},
  // Shell-hosting root: fills the screen so RoyaleLobbyShell's flex:1 layout and
  // its internally-scrolling rail both get a bounded height.
  rootFlex: {flex: 1, minHeight: 0, backgroundColor: BG_PAGE},
  content: {padding: vs(4), gap: vs(12)},
  centered: {flex: 1, alignItems: 'center', justifyContent: 'center', padding: 40},
  card: {
    backgroundColor: CCColors.White,
    borderRadius: 14,
    padding: vs(16),
    gap: vs(10),
    borderWidth: 1,
    borderColor: BORDER,
  },
  cancelledCard: {alignItems: 'center'},
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: vs(10),
    borderRadius: 10,
    backgroundColor: 'rgba(235,87,87,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(235,87,87,0.25)',
  },
  cardTitle: {
    fontSize: FontSizes[12],
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: BROWN,
  },
  headerRow: {flexDirection: 'row', alignItems: 'flex-start', gap: 12},
  headerRight: {alignItems: 'flex-end', gap: 8},
  copyLinkBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: TEAL,
    backgroundColor: TEAL_TINT_SOFT,
  },
  statePill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
  infoChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: TEAL_TINT_SOFT,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 12,
    paddingVertical: 14,
    marginTop: vs(4),
  },
  registerBtn: {backgroundColor: CCColors.Primary.Action},
  withdrawBtn: {backgroundColor: CCColors.WrongMoves?.Background || '#EB5757'},
  liveBtn: {backgroundColor: '#E17846'},
  btnDisabled: {opacity: 0.5},
  // ── Race-lobby layout (scheduled): matches PuzzleRoyaleBattle EXACTLY ───
  lobbyRow: {flexDirection: 'column', gap: vs(12)},
  lobbyRowWide: {flexDirection: 'row', alignItems: 'flex-start'},
  boardCol: {flex: 1, minWidth: 0, gap: vs(12)},
  boardWrap: {
    width: '100%',
    maxWidth: 560,
    aspectRatio: 1,
    alignSelf: 'center',
  },
  // Title card above the board — battle-lobby `waitCard`.
  waitCard: {
    backgroundColor: CCColors.White,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#EDE4CC',
    paddingHorizontal: 18,
    paddingVertical: 14,
  },
  waitTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  waitCardTitle: {
    fontSize: FontSizes[18],
    fontWeight: '800',
    color: BROWN,
    flexShrink: 1,
  },
  waitChips: {flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10},
  waitChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(61, 171, 158, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(61, 171, 158, 0.30)',
  },
  waitChipText: {fontSize: FontSizes[13], fontWeight: '700', color: '#2E8B7F'},
  waitChipCap: {textTransform: 'capitalize'},
  // Matches PuzzleRoyaleBattle's `rail` EXACTLY (was width:'100%' + vs(12),
  // which made the tab column size differently from the live lobby).
  // minHeight:0 — see pitfall 116. Without it this flex column can't shrink
  // below its content, so an expanded Details accordion grows the rail (and then
  // the page) instead of letting the rail's ScrollView take over.
  rail: {gap: 12, flex: 1, minHeight: 0},
  railWide: {width: 360, flexShrink: 0},
  // Tab bar — battle-lobby `railTabs`.
  railTabs: {
    flexDirection: 'row',
    gap: 4,
    backgroundColor: '#F4EEDB',
    borderRadius: 10,
    padding: 4,
  },
  railTab: {flex: 1, paddingVertical: 9, borderRadius: 7, alignItems: 'center'},
  railTabActive: {backgroundColor: TEAL},
  railTabText: {fontSize: FontSizes[12], fontWeight: '700', color: '#8A7E5F'},
  railTabTextActive: {color: '#fff'},
  // Matches PuzzleRoyaleBattle's `railBody` EXACTLY (was vs(10) + a minHeight,
  // which changed the tab-body spacing vs the live lobby).
  // flexGrow so the tab body absorbs the rail's spare height and pushes the
  // Register/Withdraw footer to the BOTTOM of the rail column (the ad-hoc lobby
  // puts its Start button there), instead of letting it float directly under
  // whatever the active tab happens to render.
  railBody: {gap: 12, flexGrow: 1, minHeight: 0},
  railFooter: {
    borderTopWidth: 1,
    borderTopColor: BORDER,
    paddingTop: vs(10),
    marginTop: vs(2),
  },
  scoringHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tabBody: {gap: vs(10), paddingTop: vs(4)},
  // Modern card surface for the Players / Spectators / Details tabs (arena-ish:
  // white panel, soft radius, hairline border, roomy padding). NOT applied to
  // the Race tab, which keeps its bare countdown treatment by request.
  // Arena-style panel: white card, generous radius, hairline warm border and a
  // very soft lifted shadow (matches the arena leaderboard/details cards, but
  // expressed with royale's own constants rather than importing that theme).
  tabCard: {
    backgroundColor: CCColors.White,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: LINE,
    padding: vs(16),
    gap: vs(10),
    // flex (not flexGrow) + minHeight:0 so the card can also SHRINK. flexGrow
    // alone let it keep growing, which pushed the rail and then the page (the
    // "Details tab is broken" report). Scrolling is handled by the rail's own
    // ScrollView, so no overflow here — clipping would cut off the card shadow.
    flex: 1,
    minHeight: 0,
    shadowColor: '#2A2317',
    shadowOpacity: 0.05,
    shadowRadius: 12,
    shadowOffset: {width: 0, height: 3},
    elevation: 2,
  },
  tabSectionTitle: {
    fontSize: FontSizes[11],
    fontWeight: '800',
    color: MUTED,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: vs(8),
    paddingVertical: vs(26),
  },
  countdownBlock: {
    gap: vs(2),
    paddingBottom: vs(10),
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  // Tournament-info block (Details tab): label/value rows.
  infoBlock: {
    gap: 0,
    paddingBottom: vs(10),
    borderBottomWidth: 1,
    borderBottomColor: LINE,
  },
  // Be-here-early notice: icon + wrapping copy, sitting below the info rows.
  // `flex: 1` on the text so it wraps instead of overflowing the rail, and
  // alignItems:'flex-start' so the icon stays on the FIRST line of that wrap.
  beEarlyRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    paddingTop: vs(10),
  },
  beEarlyText: {
    flex: 1,
    fontSize: FontSizes[12],
    lineHeight: 17,
    color: CCColors.TextColor.GreyText,
  },
  // Hairline separators make the label/value pairs read as a table rather than
  // loose floating text. `:first-child` isn't available in RN, so the block's
  // own paddingTop absorbs the first row's top border visually.
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: vs(7),
    borderTopWidth: 1,
    borderTopColor: LINE,
  },
  detailLabel: {fontSize: FontSizes[12], color: MUTED, flexShrink: 0},
  detailValue: {
    fontSize: FontSizes[12],
    fontWeight: '700',
    color: TEXT_DARK,
    textTransform: 'capitalize',
    // Shrink + truncate rather than overflowing the rail's right edge.
    flexShrink: 1,
    textAlign: 'right',
  },
  shareRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: vs(8),
    paddingHorizontal: vs(10),
    borderRadius: 10,
    borderWidth: 1,
    borderColor: TEAL,
    backgroundColor: TEAL_TINT_SOFT,
    alignSelf: 'flex-start',
  },
  ruleList: {gap: vs(8), marginTop: vs(4)},
  ruleRow: {flexDirection: 'row', alignItems: 'flex-start', gap: 8},
  descBlock: {
    backgroundColor: TEAL_TINT_SOFT,
    borderRadius: 10,
    padding: vs(10),
    borderWidth: 1,
    borderColor: BORDER,
  },
  ladderTable: {
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 10,
    overflow: 'hidden',
  },
  ladderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: BORDER,
    paddingVertical: vs(7),
    paddingHorizontal: vs(10),
  },
  ladderHead: {borderTopWidth: 0, backgroundColor: TEAL_TINT_SOFT},
  ladderCellL: {flex: 1, fontSize: FontSizes[12], color: TEXT_DARK},
  ladderCellR: {
    width: 90,
    fontSize: FontSizes[12],
    color: TEXT_DARK,
    textAlign: 'right',
    fontWeight: '600',
  },
  ladderCellRWrap: {width: 90, alignItems: 'flex-end'},
  ladderPts: {
    fontSize: FontSizes[13],
    color: TEAL_DARK,
    fontWeight: '700',
  },
  registeredNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: vs(4),
  },
  rosterList: {gap: vs(6)},
  rosterRowCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: vs(6),
    paddingHorizontal: vs(8),
    borderRadius: 10,
    backgroundColor: TEAL_TINT_SOFT,
  },
  rosterAvatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: TEAL,
    alignItems: 'center',
    justifyContent: 'center',
  },
  youTag: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    backgroundColor: 'rgba(61, 171, 158, 0.16)',
  },
});
